/**
 * CitationMark — extensión TipTap para marcadores de cita IA `[N]`.
 *
 * Implementa el ADR-0001 Tier 1. El texto plano sigue siendo `[6]` —
 * compatible con copy/paste, exports markdown, búsqueda full-text — y
 * encima se decora con `<cite data-ref-number="6">[6]</cite>`. Click /
 * hover en el `<cite>` dispara el popover de fuente que vive en el
 * viewer.
 *
 * Decisiones de diseño:
 *
 *   - **Mark vs Node:** preferimos Mark porque el marcador NO altera la
 *     semántica del texto, solo lo decora. Si fuera Node tendríamos que
 *     manejar serialización ad-hoc en exports (markdown, docx, pdf).
 *
 *   - **inclusive: false:** evita que el cursor "extienda" la marca al
 *     escribir alrededor del `[N]`. Si el user agrega texto justo
 *     después, no queda marcado como cita.
 *
 *   - **renderHTML:** sin click handler — el viewer (`OfficialViewer`)
 *     maneja el click via event delegation desde un wrapper, evitando
 *     que cada mark cargue su propio listener (mejor para perf con
 *     decenas de citas por sección).
 *
 *   - **parseHTML:** matchea `cite[data-ref-number]` para round-trips
 *     y eventual import desde docs externos. La spec de CSS también
 *     permite que estilos como `cite { font-style: italic }` se apliquen
 *     por default.
 */

import { Mark, mergeAttributes } from '@tiptap/core'

export interface CitationMarkOptions {
  HTMLAttributes: Record<string, unknown>
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    citationMark: {
      setCitation: (refNumber: number) => ReturnType
      unsetCitation: () => ReturnType
    }
  }
}

export const CitationMark = Mark.create<CitationMarkOptions>({
  name: 'citation',
  inclusive: false,
  spanning: false,
  excludes: '',

  addOptions() {
    return {
      HTMLAttributes: {},
    }
  },

  addAttributes() {
    return {
      refNumber: {
        default: null,
        parseHTML: (element) => {
          const raw = element.getAttribute('data-ref-number')
          if (raw === null) return null
          const n = Number.parseInt(raw, 10)
          return Number.isFinite(n) && n > 0 ? n : null
        },
        renderHTML: (attrs) => {
          const value = attrs['refNumber']
          if (typeof value !== 'number' || !Number.isFinite(value)) {
            return {}
          }
          return { 'data-ref-number': String(value) }
        },
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'cite[data-ref-number]',
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'cite',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        class: 'hiva-citation',
      }),
      0,
    ]
  },

  addCommands() {
    return {
      setCitation:
        (refNumber: number) =>
        ({ commands }) =>
          commands.setMark(this.name, { refNumber }),
      unsetCitation:
        () =>
        ({ commands }) =>
          commands.unsetMark(this.name),
    }
  },
})

/** Regex que matchea marcadores `[N]` (1+ dígitos entre corchetes).
 * Mismo patrón que `_CITATION_INLINE_RE` del backend. Lookbehind para
 * evitar matchear `[[1]]` (arrays anidados que podrían venir de tablas).
 */
export const CITATION_INLINE_RE = /(?<!\[)\[(\d+)\](?!\])/g
